"use client"

import * as React from 'react'
import Link from 'next/link'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { ErrorMessage, LoadingMessage } from '@open-mercato/ui/backend/detail'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { FormHeader } from '@open-mercato/ui/backend/forms'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { apiCallOrThrow, readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { Button } from '@open-mercato/ui/primitives/button'
import { Input } from '@open-mercato/ui/primitives/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@open-mercato/ui/primitives/tabs'
import { Textarea } from '@open-mercato/ui/primitives/textarea'

type ECmrDetail = Record<string, unknown> & {
  id: string
  documentNumber: string
  status: string
  issueDate: string
  referenceNumber: string | null
  placeOfTakingOver: string
  placeOfDelivery: string
  goodsDescription: string
  currentSealHash: string
  senderSnapshot: Record<string, unknown>
  carrierSnapshot: Record<string, unknown>
  receiverSnapshot: Record<string, unknown>
  signatures: Array<{
    id: string
    role: string
    signerName: string
    signerCompany: string
    signedAt: string
  }>
  deliveryEvidence: {
    deliveredAt: string
    receiverName: string
    receiverCompany: string | null
    notes: string | null
    receiverReservationSlotId: string | null
  } | null
}

type QrArtifact = {
  svg: string
}

async function fetchDetail(id: string): Promise<ECmrDetail> {
  return readApiResultOrThrow<ECmrDetail>(`/api/road/ecmr/${id}`, undefined, {
    errorMessage: 'Failed to fetch eCMR detail.',
  })
}

async function fetchQr(id: string): Promise<QrArtifact> {
  return readApiResultOrThrow<QrArtifact>(`/api/road/ecmr/${id}/qr`, undefined, {
    errorMessage: 'Failed to fetch QR artifact.',
  })
}

function canSenderSign(detail: ECmrDetail) {
  return detail.status === 'DRAFT' && !detail.signatures.some((item) => item.role === 'SENDER')
}

function canCarrierSign(detail: ECmrDetail) {
  return detail.status === 'ISSUED' && !detail.signatures.some((item) => item.role === 'CARRIER')
}

function canDeliver(detail: ECmrDetail) {
  return detail.status === 'IN_TRANSIT' && !detail.signatures.some((item) => item.role === 'RECEIVER')
}

function canArchive(detail: ECmrDetail) {
  return detail.status === 'DELIVERED'
}

export default function ECmrDetailPage({ params }: { params: { id: string } }) {
  const t = useT()
  const queryClient = useQueryClient()
  const scopeVersion = useOrganizationScopeVersion()
  const { runMutation } = useGuardedMutation({ contextId: `ecmr-${params.id}` })
  const [activeTab, setActiveTab] = React.useState('summary')
  const [signerName, setSignerName] = React.useState('')
  const [signerCompany, setSignerCompany] = React.useState('')
  const [receiverName, setReceiverName] = React.useState('')
  const [receiverCompany, setReceiverCompany] = React.useState('')
  const [reservationSlotId, setReservationSlotId] = React.useState('')
  const [deliveryNotes, setDeliveryNotes] = React.useState('')

  const detailQuery = useQuery({
    queryKey: ['ecmr', 'detail', params.id, scopeVersion],
    queryFn: () => fetchDetail(params.id),
  })

  const qrQuery = useQuery({
    queryKey: ['ecmr', 'qr', params.id, scopeVersion],
    queryFn: () => fetchQr(params.id),
    enabled: detailQuery.isSuccess,
  })

  const refresh = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['ecmr', 'detail', params.id] })
    await queryClient.invalidateQueries({ queryKey: ['ecmr', 'qr', params.id] })
    await queryClient.invalidateQueries({ queryKey: ['ecmr', 'list'] })
  }, [params.id, queryClient])

  const detail = detailQuery.data

  const signDocument = React.useCallback(async (role: 'SENDER' | 'CARRIER') => {
    if (!detail) return
    await runMutation({
      context: { kind: 'road.ecmr', record: { id: detail.id } },
      mutationPayload: { role, signerName, signerCompany },
      operation: async () => {
        await apiCallOrThrow(`/api/road/ecmr/${detail.id}/sign`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            role,
            signerName,
            signerCompany,
          }),
        }, {
          errorMessage: t('ecmr.messages.signError', 'Failed to sign eCMR.'),
        })
      },
    })
    flash(t('ecmr.messages.signSuccess', 'eCMR signed.'), 'success')
    await refresh()
  }, [detail, refresh, runMutation, signerCompany, signerName, t])

  const deliverDocument = React.useCallback(async () => {
    if (!detail) return
    await runMutation({
      context: { kind: 'road.ecmr', record: { id: detail.id } },
      mutationPayload: { receiverName, receiverCompany, reservationSlotId, deliveryNotes },
      operation: async () => {
        await apiCallOrThrow(`/api/road/ecmr/${detail.id}/deliver`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            deliveredAt: new Date().toISOString(),
            receiverName,
            receiverCompany,
            receiverReservationSlotId: reservationSlotId || null,
            notes: deliveryNotes || null,
          }),
        }, {
          errorMessage: t('ecmr.messages.deliverError', 'Failed to deliver eCMR.'),
        })
      },
    })
    flash(t('ecmr.messages.deliverSuccess', 'eCMR delivered.'), 'success')
    await refresh()
  }, [deliveryNotes, detail, receiverCompany, receiverName, refresh, reservationSlotId, runMutation, t])

  const archiveDocument = React.useCallback(async () => {
    if (!detail) return
    await runMutation({
      context: { kind: 'road.ecmr', record: { id: detail.id } },
      mutationPayload: { id: detail.id },
      operation: async () => {
        await apiCallOrThrow(`/api/road/ecmr/${detail.id}/archive`, {
          method: 'POST',
        }, {
          errorMessage: t('ecmr.messages.archiveError', 'Failed to archive eCMR.'),
        })
      },
    })
    flash(t('ecmr.messages.archiveSuccess', 'eCMR archived.'), 'success')
    await refresh()
  }, [detail, refresh, runMutation, t])

  if (detailQuery.isLoading && !detail) {
    return (
      <Page>
        <PageBody>
          <LoadingMessage label={t('ecmr.messages.loadingDetail', 'Loading eCMR detail...')} />
        </PageBody>
      </Page>
    )
  }

  if (detailQuery.isError || !detail) {
    return (
      <Page>
        <PageBody>
          <ErrorMessage
            label={t('ecmr.messages.loadErrorTitle', 'Could not load eCMR')}
            description={t('ecmr.messages.loadDetailError', 'The document could not be loaded.')}
          />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <FormHeader
        mode="detail"
        backHref="/backend/ecmr"
        entityTypeLabel={t('ecmr.pages.list.title', 'eCMR')}
        title={detail.documentNumber}
        subtitle={detail.referenceNumber ?? undefined}
        statusBadge={<Badge>{detail.status}</Badge>}
      />
      <PageBody className="space-y-6">
        <div className="flex flex-wrap gap-3">
          <Button asChild>
            <Link href={`/api/road/ecmr/${detail.id}/pdf`} target="_blank">{t('ecmr.actions.pdf', 'PDF')}</Link>
          </Button>
          <Button type="button" variant="outline" onClick={() => { void refresh() }}>
            {t('ecmr.actions.refresh', 'Refresh')}
          </Button>
          {canArchive(detail) ? (
            <Button type="button" variant="secondary" onClick={() => { void archiveDocument() }}>
              {t('ecmr.actions.archive', 'Archive')}
            </Button>
          ) : null}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="summary">{t('ecmr.tabs.summary', 'Summary')}</TabsTrigger>
            <TabsTrigger value="workflow">{t('ecmr.tabs.workflow', 'Workflow')}</TabsTrigger>
            <TabsTrigger value="qr">{t('ecmr.tabs.qr', 'QR')}</TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border p-4">
                <h3 className="font-medium">{t('ecmr.detail.route', 'Route')}</h3>
                <p>{detail.placeOfTakingOver}</p>
                <p>{detail.placeOfDelivery}</p>
              </div>
              <div className="rounded-lg border p-4">
                <h3 className="font-medium">{t('ecmr.detail.goods', 'Goods')}</h3>
                <p>{detail.goodsDescription}</p>
                <p>{t('ecmr.detail.seal', 'Seal')}: {detail.currentSealHash}</p>
              </div>
              <div className="rounded-lg border p-4">
                <h3 className="font-medium">{t('ecmr.detail.sender', 'Sender')}</h3>
                <p>{String(detail.senderSnapshot.name ?? '-')}</p>
              </div>
              <div className="rounded-lg border p-4">
                <h3 className="font-medium">{t('ecmr.detail.carrier', 'Carrier')}</h3>
                <p>{String(detail.carrierSnapshot.name ?? '-')}</p>
              </div>
              <div className="rounded-lg border p-4">
                <h3 className="font-medium">{t('ecmr.detail.receiver', 'Receiver')}</h3>
                <p>{String(detail.receiverSnapshot.name ?? '-')}</p>
              </div>
              <div className="rounded-lg border p-4">
                <h3 className="font-medium">{t('ecmr.detail.signatures', 'Signatures')}</h3>
                {detail.signatures.map((item) => (
                  <p key={item.id}>{item.role}: {item.signerName}</p>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="workflow" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border p-4 space-y-3">
                <h3 className="font-medium">{t('ecmr.workflow.sign', 'Sign')}</h3>
                <Input value={signerName} onChange={(event) => setSignerName(event.target.value)} placeholder={t('ecmr.workflow.signerName', 'Signer name')} />
                <Input value={signerCompany} onChange={(event) => setSignerCompany(event.target.value)} placeholder={t('ecmr.workflow.signerCompany', 'Signer company')} />
                <div className="flex gap-2">
                  <Button type="button" disabled={!canSenderSign(detail) || !signerName || !signerCompany} onClick={() => { void signDocument('SENDER') }}>
                    {t('ecmr.actions.signSender', 'Sign as sender')}
                  </Button>
                  <Button type="button" variant="outline" disabled={!canCarrierSign(detail) || !signerName || !signerCompany} onClick={() => { void signDocument('CARRIER') }}>
                    {t('ecmr.actions.signCarrier', 'Sign as carrier')}
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border p-4 space-y-3">
                <h3 className="font-medium">{t('ecmr.workflow.deliver', 'Deliver')}</h3>
                <Input value={receiverName} onChange={(event) => setReceiverName(event.target.value)} placeholder={t('ecmr.workflow.receiverName', 'Receiver signer name')} />
                <Input value={receiverCompany} onChange={(event) => setReceiverCompany(event.target.value)} placeholder={t('ecmr.workflow.receiverCompany', 'Receiver company')} />
                <Input value={reservationSlotId} onChange={(event) => setReservationSlotId(event.target.value)} placeholder={t('ecmr.workflow.reservationSlotId', 'Reservation slot ID (optional)')} />
                <Textarea value={deliveryNotes} onChange={(event) => setDeliveryNotes(event.target.value)} placeholder={t('ecmr.workflow.deliveryNotes', 'Delivery notes')} />
                <Button type="button" disabled={!canDeliver(detail) || !receiverName} onClick={() => { void deliverDocument() }}>
                  {t('ecmr.actions.deliver', 'Deliver and sign')}
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="qr">
            <div className="rounded-lg border p-4">
              {qrQuery.data?.svg ? (
                <div dangerouslySetInnerHTML={{ __html: qrQuery.data.svg }} />
              ) : (
                <LoadingMessage label={t('ecmr.messages.loadingQr', 'Loading QR...')} />
              )}
            </div>
          </TabsContent>
        </Tabs>
      </PageBody>
    </Page>
  )
}
